import { ENV } from '@/config/env';
import { StockChartData } from '@/types/stock.types';
import { CHART_TIMEFRAMES, ChartTimeframe } from '@/constants/chartTimeframes';
import { ChartResolution, RESOLUTION_CONFIG } from '@/constants/chartResolutions';

/**
 * Polygon.io Chart Data Service
 *
 * Fetches historical stock data with flexible timeframes using Polygon aggregates API
 * Starter Plan ($29/month): Unlimited API calls, 5 years history, 15-min delayed
 *
 * API Endpoint: /v2/aggs/ticker/{ticker}/range/{multiplier}/{timespan}/{from}/{to}
 */

// ==================== TYPES ====================

interface PolygonAggregateBar {
  o: number; // Open price
  h: number; // High price
  l: number; // Low price
  c: number; // Close price
  v: number; // Volume
  t: number; // Timestamp (milliseconds)
  vw?: number; // Volume weighted average price
  n?: number; // Number of transactions
}

interface PolygonAggregatesResponse {
  ticker: string;
  status: string;
  adjusted: boolean;
  queryCount: number;
  resultsCount: number;
  results?: PolygonAggregateBar[];
}

// ==================== SERVICE ====================

class PolygonChartDataService {
  private readonly baseUrl: string;
  private readonly apiKey: string;
  private memoryCache: Map<string, { data: StockChartData[]; expires: number }> = new Map();

  constructor() {
    this.baseUrl = ENV.POLYGON_API_URL || 'https://api.polygon.io';
    this.apiKey = ENV.POLYGON_API_KEY || '';

    if (!this.apiKey) {
      console.warn('⚠️ [PolygonChart] API key not configured. Set POLYGON_API_KEY in .env');
    }
  }

  /**
   * Check if API is configured
   */
  isConfigured(): boolean {
    return !!this.apiKey;
  }

  /**
   * Get chart data for a specific timeframe
   *
   * @param ticker - Stock symbol (e.g., "AAPL")
   * @param timeframe - Chart timeframe ('1D', '1W', '1M', etc.)
   * @param useCache - Whether to use memory cache (default: true)
   * @returns Array of chart data points or error
   */
  async getChartData(
    ticker: string,
    timeframe: ChartTimeframe,
    useCacheOrOptions: boolean | { useCache?: boolean; resolution?: ChartResolution } = true
  ): Promise<{ data: StockChartData[]; error: string | null }> {
    try {
      if (!this.isConfigured()) {
        return {
          data: [],
          error: 'Polygon API key not configured',
        };
      }

      const normalizedTicker = ticker.trim().toUpperCase();
      const config = CHART_TIMEFRAMES[timeframe];

      if (!config) {
        return {
          data: [],
          error: `Invalid timeframe: ${timeframe}`,
        };
      }

      // Resolve options (backward compatible with boolean third param)
      const resolvedUseCache =
        typeof useCacheOrOptions === 'boolean'
          ? useCacheOrOptions
          : (useCacheOrOptions.useCache ?? true);
      const resolution =
        typeof useCacheOrOptions === 'object' ? useCacheOrOptions.resolution : undefined;

      // Determine resolution to use (override -> default from timeframe)
      const resCfg = resolution
        ? RESOLUTION_CONFIG[resolution]
        : ({ multiplier: config.multiplier, timespan: config.timespan } as const);

      // Check cache first
      const cacheKey = `${normalizedTicker}:${timeframe}:${resCfg.timespan}${resCfg.multiplier}`;
      if (resolvedUseCache && this.memoryCache.has(cacheKey)) {
        const cached = this.memoryCache.get(cacheKey)!;
        if (Date.now() < cached.expires) {
          console.log(`✅ [PolygonChart] Cache hit for ${normalizedTicker} ${timeframe}`);
          return { data: cached.data, error: null };
        }
        this.memoryCache.delete(cacheKey);
      }

      // Calculate date range
      const { from, to } = this.calculateDateRange(config.days);

      console.log(`📊 [PolygonChart] Fetching ${timeframe} data for ${normalizedTicker}...`);
      console.log(`📅 Date range: ${from} to ${to}`);

      // Build API URL
      const url = `${this.baseUrl}/v2/aggs/ticker/${normalizedTicker}/range/${resCfg.multiplier}/${resCfg.timespan}/${from}/${to}?adjusted=true&sort=asc&apiKey=${this.apiKey}`;

      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(`❌ [PolygonChart] API error (${response.status}):`, errorText);

        if (response.status === 429) {
          return { data: [], error: 'Rate limit exceeded. Please try again in a moment.' };
        }
        if (response.status === 401 || response.status === 403) {
          return { data: [], error: 'Invalid API key or access denied.' };
        }
        if (response.status === 404) {
          return { data: [], error: 'Stock symbol not found.' };
        }

        return { data: [], error: `API error: ${response.status}` };
      }

      const json: PolygonAggregatesResponse = await response.json();

      // Smart fallback for intraday: if today has no data (weekend/holiday), try previous business day
      if (timeframe === '1D' && (!json.results || json.results.length === 0)) {
        const prev = this.getPreviousBusinessDay(new Date());
        const fallbackUrl = `${this.baseUrl}/v2/aggs/ticker/${normalizedTicker}/range/${resCfg.multiplier}/${resCfg.timespan}/${prev}/${prev}?adjusted=true&sort=asc&apiKey=${this.apiKey}`;
        try {
          const fallbackResp = await fetch(fallbackUrl);
          if (fallbackResp.ok) {
            const fallbackJson: PolygonAggregatesResponse = await fallbackResp.json();
            if (fallbackJson.status === 'OK' || fallbackJson.status === 'DELAYED') {
              json.results = fallbackJson.results;
              console.log(
                `Fallback loaded ${fallbackJson.results?.length || 0} bars for previous business day ${prev}`
              );
            }
          }
        } catch {}
      }

      // Accept both 'OK' and 'DELAYED' status (Starter plan has 15-min delayed data)
      if (json.status !== 'OK' && json.status !== 'DELAYED') {
        console.error('❌ [PolygonChart] Invalid response status:', json.status);
        return { data: [], error: 'Invalid API response' };
      }

      if (json.status === 'DELAYED') {
        console.log('⏰ [PolygonChart] Using delayed data (15-min delay - Starter plan)');
      }

      if (!json.results || json.results.length === 0) {
        console.warn('⚠️ [PolygonChart] No data returned for', normalizedTicker, timeframe);
        return { data: [], error: 'No chart data available for this timeframe' };
      }

      // Transform to our StockChartData format
      const chartData: StockChartData[] = json.results.map(bar =>
        this.transformBar(bar, (resCfg as any).timespan || config.timespan)
      );

      console.log(
        `✅ [PolygonChart] Fetched ${chartData.length} data points for ${normalizedTicker} ${timeframe}`
      );

      // Cache the result
      this.memoryCache.set(cacheKey, {
        data: chartData,
        expires: Date.now() + config.cacheTTL,
      });

      return { data: chartData, error: null };
    } catch (error) {
      console.error('❌ [PolygonChart] Error fetching chart data:', error);
      return {
        data: [],
        error: error instanceof Error ? error.message : 'Unknown error fetching chart data',
      };
    }
  }

  /**
   * Transform Polygon bar to our StockChartData format
   */
  private transformBar(bar: PolygonAggregateBar, timespan: string): StockChartData {
    // Convert Unix timestamp (milliseconds) to ISO date string
    const date = new Date(bar.t).toISOString();

    return {
      date,
      open: bar.o,
      high: bar.h,
      low: bar.l,
      close: bar.c,
      volume: bar.v,
    };
  }

  /**
   * Calculate date range for API request
   * Returns dates in YYYY-MM-DD format
   */
  private calculateDateRange(daysBack: number): { from: string; to: string } {
    const now = new Date();
    const to = this.formatDate(now);

    // For intraday (1D), get today's date
    // For multi-day, go back N days
    const from = new Date(now);
    from.setDate(from.getDate() - daysBack);

    return {
      from: this.formatDate(from),
      to,
    };
  }

  /**
   * Format date to YYYY-MM-DD
   */
  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Get previous business day (Mon–Fri) as YYYY-MM-DD
   */
  private getPreviousBusinessDay(fromDate: Date): string {
    const d = new Date(fromDate);
    do {
      d.setDate(d.getDate() - 1);
    } while (d.getDay() === 0 || d.getDay() === 6); // Skip Sun(0) and Sat(6)
    return this.formatDate(d);
  }

  /**
   * Clear memory cache (useful for forcing refresh)
   */
  clearCache(): void {
    this.memoryCache.clear();
    console.log('🗑️ [PolygonChart] Memory cache cleared');
  }

  /**
   * Clear cache for specific ticker and timeframe
   */
  clearCacheFor(ticker: string, timeframe?: ChartTimeframe): void {
    const normalizedTicker = ticker.trim().toUpperCase();

    if (timeframe) {
      const cacheKey = `${normalizedTicker}:${timeframe}`;
      this.memoryCache.delete(cacheKey);
      console.log(`🗑️ [PolygonChart] Cleared cache for ${cacheKey}`);
    } else {
      // Clear all timeframes for this ticker
      const keysToDelete: string[] = [];
      this.memoryCache.forEach((_, key) => {
        if (key.startsWith(`${normalizedTicker}:`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => this.memoryCache.delete(key));
      console.log(
        `🗑️ [PolygonChart] Cleared ${keysToDelete.length} cache entries for ${normalizedTicker}`
      );
    }
  }
}

// Export singleton instance
export const polygonChartDataService = new PolygonChartDataService();
